import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useEditCategories, useEditContent, useGetContent } from 'src/components/hooks';
import { useSpinner } from 'src/components/hooks/useExtraStore';
import { useFormik, FormikProvider, Form, ErrorMessage } from 'formik';
import { Grid } from '@mui/material';
import {
  Box,
  Label,
  Button,
  Typography,
  Drawer,
  FormControl,
  TextField,
  Switch,
} from 'src/components/ui';
import { imageUpload, updateContent } from './UploadImage';

export const ContentDrawer = ({ contentId }) => {
  const { data: contentData, resetContent } = useGetContent();
  const { isDrawerOpen, setIsDrawerOpen } = useEditContent();
  const { editCategories } = useEditCategories();
  const { setSpinner } = useSpinner();
  const [locale, setLocale] = useState('en');
  const [images, setImages] = useState([]);
  const [previousContentId, setPreviousContentId] = useState(null);

  const formik = useFormik({
    initialValues: {
      en: {
        name: '',
        description: '',
        recommended_for: '',
      },
      ka: {
        name: '',
        description: '',
        recommended_for: '',
      },
      category: {
        position: '',
        price_from: '',
        requestable: false,
      },
    },
    validateOnChange: false,
    validateOnBlur: false,

    onSubmit: async (values) => {
      const payloadEn = {
        name: values.en.name,
        description: values.en.description,
        recommended_for: values.en.recommended_for,
        images: images?.map((item) => ({ id: item.id })),
      };

      const payloadKa = {
        name: values.ka.name,
        description: values.ka.description,
        recommended_for: values.ka.recommended_for,
        images: images?.map((item) => ({ id: item.id })),
      };

      const { position, price_from, requestable } = values.category;

      const categoryData = {
        position: Number(position),
        price_from: Number(price_from),
        requestable: Boolean(requestable),
      };

      updateContent(contentId, payloadEn, 'en');
      // updateContent(contentId, payloadKa, 'ka');
      editCategories(categoryData, contentId);

      setIsDrawerOpen(false);
    },
  });

  const handleOpenedDrawer = () => {
    setLocale('en');
    setIsDrawerOpen(false);
    setImages([]);
    formik.resetForm();
    resetContent();
  };

  useEffect(() => {
    if (contentId && contentId !== previousContentId) {
      formik.resetForm();
      setImages([]);
      setPreviousContentId(contentId);
    }
  }, [contentId]);

  useEffect(() => {
    if (!contentData) return;
    setSpinner(true);

    const enTranslation =
      contentData.translations?.find((t) => t.locale === 'en') ||
      contentData.translations?.[0];
    const kaTranslation = contentData.translations?.find((t) => t.locale === 'ka');

    formik.setValues({
      en: {
        name: enTranslation?.name || contentData.name || '',
        description: enTranslation?.description || contentData.description || '',
        recommended_for:
          enTranslation?.recommended_for || contentData.recommended_for || '',
      },
      ka: {
        name: kaTranslation?.name || '',
        description: kaTranslation?.description || '',
        recommended_for: kaTranslation?.recommended_for || '',
      },
      category: contentData.category || {
        position: '',
        price_from: '',
      },
    });

    setImages(contentData.images || []);
  }, [contentData]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(`${locale}.${name}`, value);
  };

  const handleCategoryChange = (field, value) => {
    formik.setFieldValue(`category.${field}`, value);
  };

  const handleImageUpload = (e) => {
    setSpinner(true);
    imageUpload(e, setImages);
  };

  const handleDeleteImage = (id) => {
    setImages((prevImages) => prevImages.filter((image) => image.id !== id));
  };

  if (!isDrawerOpen) {
    return null;
  }

  const currentValues = formik.values[locale];

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={handleOpenedDrawer}
      PaperProps={{
        sx: {
          width: '30%',
          '@media (max-width: 600px)': {
            width: '70%',
          },
        },
      }}
    >
      <FormikProvider value={formik}>
        <Form
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '24px',
          }}
          onSubmit={formik.handleSubmit}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: '24px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography>Content</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 1 }}>
                  EN
                </Typography>
                <Switch
                  checked={locale === 'ka'}
                  onChange={(e) => setLocale(e.target.checked ? 'ka' : 'en')}
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  GE
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3} sx={{ marginTop: '24px' }}>
              <>
                <Grid item xs={12} sm={12}>
                  <FormControl fullWidth>
                    <TextField
                      name="name"
                      label="Name"
                      value={currentValues.name}
                      onChange={handleFieldChange}
                    />
                    <ErrorMessage name={`${locale}.name`} component="div" />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <FormControl fullWidth>
                    <TextField
                      name="recommended_for"
                      label="Recommended For"
                      value={currentValues.recommended_for}
                      onChange={handleFieldChange}
                    />
                    <ErrorMessage name={`${locale}.recommended_for`} component="div" />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <FormControl fullWidth>
                    <TextField
                      name="description"
                      label="Description"
                      value={currentValues.description}
                      onChange={handleFieldChange}
                      multiline
                      rows={3}
                    />
                    <ErrorMessage name={`${locale}.description`} component="div" />
                  </FormControl>
                </Grid>
              </>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <FormControl fullWidth sx={{ marginTop: '24px', width: '48%' }}>
                <TextField
                  name="position"
                  label="Position"
                  type="number"
                  value={formik.values.category?.position}
                  onChange={(e) => handleCategoryChange('position', e.target.value)}
                />
              </FormControl>

              <FormControl fullWidth sx={{ marginTop: '24px', width: '48%' }}>
                <TextField
                  name="price_from"
                  label="Price from"
                  type="number"
                  value={formik.values.category?.price_from}
                  onChange={(e) => handleCategoryChange('price_from', e.target.value)}
                />
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '24px' }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Requestable
              </Typography>
              <Switch
                checked={formik.values.category?.requestable}
                onChange={(e) => handleCategoryChange('requestable', e.target.checked)}
              />
            </Box>

            <Box
              sx={{ display: 'flex', marginTop: '2rem', justifyContent: 'space-between' }}
            >
              <input type="file" accept="image/*" multiple onChange={handleImageUpload} />
            </Box>

            <Box flex>
              {images?.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    marginRight: '1rem',
                    marginTop: '2rem',
                  }}
                >
                  <img
                    src={item.url}
                    alt={`Uploaded ${index}`}
                    style={{
                      maxWidth: '100%',
                      borderRadius: '1rem',
                      width: '6rem',
                      height: '6rem',
                    }}
                  />

                  <Button
                    onClick={() => handleDeleteImage(item.id)}
                    sx={{
                      position: 'absolute',
                      top: '-0.5rem',
                      right: '-0.5rem',
                      backgroundColor: 'red',
                      color: 'white',
                      borderRadius: '50%',
                      minWidth: '1.5rem',
                      height: '1.5rem',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '0',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                    }}
                  >
                    X
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              marginTop: '48px',
              position: 'absolute',
              bottom: 20,
            }}
          >
            <Button
              sx={{
                position: 'relative',
                width: '100px',
                height: '50px',
                cursor: 'pointer',
                color: 'white',
                backgroundColor: '#3B25FF',
              }}
              variant="contained"
              type="submit"
            >
              Apply
            </Button>

            <Label
              sx={{
                position: 'relative',
                minWidth: '100px',
                height: '50px',
                marginLeft: '24px',
                cursor: 'pointer',
              }}
              onClick={handleOpenedDrawer}
            >
              Cancel
            </Label>
          </Box>
        </Form>
      </FormikProvider>
    </Drawer>
  );
};

ContentDrawer.propTypes = {
  contentId: PropTypes.string,
};
